import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Message : famille "communication individuelle".
 *
 * Communication individuelle envoyée ou reçue, tous canaux confondus (email,
 * sms, notification, portail, note d'appel), avec ses destinataires et son
 * statut de remise. Construite sur le modèle "famille + type" de la bible
 * (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète du message ("rappel de
 * rendez-vous", "confirmation de commande", "réponse support") : elle est
 * portée par le discriminant `type` (chaîne libre déclarée par l'app). Le canal
 * (`channel`), le sens (`direction`) et l'état de remise (`deliveryStatus`) sont
 * des états métier À PART, distincts du discriminant (même logique que `order`
 * qui porte type + direction + status).
 *
 * Références vers d'autres familles = de simples ids (aucun import d'un autre
 * schéma) :
 *   - `fromRef` (émetteur), `toRefs` (destinataires) et `threadRef` (fil de
 *     conversation) sont volontairement polymorphes : l'émetteur ou un
 *     destinataire peut être un `contact`, un `worker` ou l'org, donc de simples
 *     ids que l'app résout selon le contexte (pas un `xxxId` typé sur une seule
 *     famille) ;
 *   - les liens de CONTEXTE (contactId, bookingId, invoiceId, ticketId,
 *     campaignId, reviewId) disent "de quoi parle ce message" et restent
 *     optionnels.
 *
 * Accès (cf. fiche) : la visibilité d'un message devrait hériter du niveau de
 * son `threadRef`, ou à défaut se borner aux rôles de l'app émettrice, pas à
 * l'org entière ; le contenu (`subject`, `body`) peut être sensible selon le
 * fil. La fiche décrit donc une POLITIQUE d'accès, pas un champ sensible isolé à
 * sortir du schéma : aucun champ n'est retiré en v0. En v0 la famille reste
 * org-wide (même doctrine que asset) ; ce cloisonnement fin viendra après.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille message (hors champs standard du socle). */
export const messageCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "reminder", "confirmation", "reply"). */
  type: z.string().min(1),
  /** Canal de la communication, chaîne libre pilotée par l'app (ex. "email", "sms", "notification", "portal", "call"). Optionnel. */
  channel: z.string().min(1).optional(),
  /** Sens de la communication, état métier piloté par l'app (ex. "inbound", "outbound"). Optionnel. */
  direction: z.string().min(1).optional(),
  /** Émetteur : id polymorphe (contact, worker, org...) résolu par l'app selon le contexte. Optionnel. */
  fromRef: z.string().min(1).optional(),
  /** Destinataires : liste d'ids (contacts, workers...). Optionnel. */
  toRefs: z.array(z.string()).optional(),
  /** Objet / sujet du message. Optionnel (un sms ou une notification peut ne pas en avoir). */
  subject: z.string().min(1).optional(),
  /** Corps / contenu du message. Optionnel. */
  body: z.string().min(1).optional(),
  /** Date d'envoi (ou de réception). Optionnel. */
  sentAt: z.date().optional(),
  /** État de remise, chaîne libre pilotée par l'app (ex. "queued", "sent", "delivered", "read", "failed"). Optionnel. */
  deliveryStatus: z.string().min(1).optional(),
  /** Fil de conversation de rattachement : id polymorphe (thread) dont l'app hérite le niveau d'accès. Optionnel. */
  threadRef: z.string().min(1).optional(),
  /** Contact concerné par le message (réf. famille contact). Optionnel. */
  contactId: z.string().min(1).optional(),
  /** Réservation / rendez-vous lié (réf. famille booking). Optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Facture liée (réf. famille invoice). Optionnel. */
  invoiceId: z.string().min(1).optional(),
  /** Ticket de support lié (réf. famille ticket). Optionnel. */
  ticketId: z.string().min(1).optional(),
  /** Campagne de rattachement (réf. famille campaign). Optionnel. */
  campaignId: z.string().min(1).optional(),
  /** Avis lié (réf. famille review). Optionnel. */
  reviewId: z.string().min(1).optional(),
} as const;

/** Schéma complet de la famille message (champs standard + champs métier). */
export const MessageSchema = z.object({
  ...baseEntityFields,
  ...messageCoreFields,
});

/** Type d'un message. */
export type Message = z.infer<typeof MessageSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const MessageCreateInputSchema = z.object({
  ...messageCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un message. */
export type MessageCreateInput = z.infer<typeof MessageCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const MessageUpdateInputSchema = z
  .object({ ...messageCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un message. */
export type MessageUpdateInput = z.infer<typeof MessageUpdateInputSchema>;
